import { SET_TOUR } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { tourOpenSelector } from '@mm/state/selectors/modalSelector';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Tour from 'reactour';
import { filteredSidebarMenuSelector } from '../filteredSidebarMenuSelector';
import './BotBuilderSidebar.scss';
import { SIDEBAR_MENU_GROUPS } from './sidebar-menu';
import { SidebarMenuGroup } from './SidebarMenuGroup/SidebarMenuGroup';
import { steps } from './Tour/Steps';
import { NewSidebarHeader } from './SidebarHeader/NewSidebarHeader/NewSidebarHeader';
import { useFreeTrialUpgradeModal } from '../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import classNames from 'classnames';
import { useClickOutside, useResolutionInfo } from '@mm/hooks';
import {
  MOBILE_SMALL,
  SIDEBAR_THRESHOLD,
} from '../../editor/constants/screenSize';
import { Overlay } from '@mm/components/src/Overlay/Overlay';
import chatbotSidebarActions from '@mm/state/modules/chatbotSidebarState/actions';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

const BotBuilderSidebar = () => {
  const filteredSidebarMenu = useSelector(filteredSidebarMenuSelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const company = useSelector(activeCompanySelector);
  const sidebarMinimized = useSelector(
    (state: StoreState) => state.chatbotSidebarState.isMinimized,
  );
  const isShopify = useSelector(selectIsShopifyContext);
  const dispatch = useDispatch();
  const tourOpenState = useSelector(tourOpenSelector);
  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;
  const showAbsoluteSidebar = screenWidth < SIDEBAR_THRESHOLD;
  const showOverlay = showAbsoluteSidebar && !sidebarMinimized;

  const setSidebarMinimized = (isMinimized: boolean) =>
    dispatch(chatbotSidebarActions.setIsMinimized(isMinimized));

  const overlayRef = useClickOutside<HTMLDivElement>(e => {
    const hasClickedMainArea = e
      .composedPath()
      .some((target: Element) =>
        target.classList?.contains('bot-builder-sidebar-overlay'),
      );

    if (hasClickedMainArea && showOverlay) {
      setSidebarMinimized(true);
    }
  });

  const onRequestCloseTour = () => {
    dispatch({
      type: SET_TOUR,
      payload: false,
    });
  };

  const menuItems = filteredSidebarMenu;

  const classList = classNames('bot-builder-sidebar', {
    'bot-builder-sidebar__absolute': showAbsoluteSidebar && !showOverlay,
    'bot-builder-sidebar__collapsed': sidebarMinimized,
    'bot-builder-sidebar__hidden': isMobile && sidebarMinimized,
  });
  const renderMenuGroup = menuGroup => (
    <SidebarMenuGroup
      key={menuGroup.id}
      menuGroup={menuGroup}
      sidebarMinimized={sidebarMinimized}
      setSidebarMinimized={setSidebarMinimized}
      activeChatbotId={activeChatbotId}
      isFreeTrialActive={isFreeTrialPromotionActive}
      showAbsoluteSidebar={showAbsoluteSidebar}
    />
  );

  const sidebarMenu = useMemo(
    () =>
      filteredSidebarMenu.length ? (
        <div className="bot-builder-sidebar__top-link-area">
          <div className="bot-builder-sidebar__top-link-area-category">
            {filteredSidebarMenu
              .filter(item => !item.category)
              .map(renderMenuGroup)}
          </div>
          {SIDEBAR_MENU_GROUPS.map(group => {
            const itemsByGroup = filteredSidebarMenu
              .filter(item => item.category === group)
              .filter(v =>
                isShopify ? !['email', 'facebook'].includes(v.id) : true,
              );
            return (
              <div
                key={uniqueId(group)}
                className={classNames(
                  'bot-builder-sidebar__top-link-area-category',
                  {
                    'bot-builder-sidebar__top-link-area-category-hidden':
                      !itemsByGroup.length,
                  },
                )}
              >
                {!sidebarMinimized ? (
                  <div className="bot-builder-sidebar__category">{group}</div>
                ) : (
                  <div className="bot-builder-sidebar__category-collapsed" />
                )}
                {itemsByGroup.map(renderMenuGroup)}
              </div>
            );
          })}
        </div>
      ) : null,
    [menuItems, sidebarMinimized, isShopify, filteredSidebarMenu],
  );

  const withOverlay = children =>
    showOverlay ? (
      <Overlay className={classNames('bot-builder-sidebar-overlay')}>
        {children}
      </Overlay>
    ) : (
      children
    );

  return (
    <>
      <Tour
        rounded={5}
        steps={steps}
        isOpen={tourOpenState}
        onRequestClose={onRequestCloseTour}
        maskSpace={15}
        showButtons={false}
        showNavigation={false}
        showCloseButton={false}
        showNumber={false}
        className="tour"
      />

      {sidebarMenu && (
        <div className="bot-builder-sidebar-wrapper">
          {withOverlay(
            <div className={classList} ref={overlayRef}>
              {activeFacebookPage && company && (
                <NewSidebarHeader
                  isFreeTrialActive={isFreeTrialPromotionActive}
                >
                  {sidebarMenu}
                </NewSidebarHeader>
              )}
            </div>,
          )}
          {!isMobile && !showAbsoluteSidebar && (
            <div
              className="bot-builder-sidebar__collapser"
              onClick={() => setSidebarMinimized(!sidebarMinimized)}
            >
              {sidebarMinimized ? (
                <RightOutlined className="bot-builder-sidebar__collapser-arrow" />
              ) : (
                <LeftOutlined className="bot-builder-sidebar__collapser-arrow" />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BotBuilderSidebar;
